/**
 * MechOnGo Automobiles — IndexedDB layer
 *
 * Provides a thin, promise-based wrapper around IndexedDB with no external
 * dependencies.  Two object stores are defined:
 *
 *   pendingRequests  — serialised fetch requests queued while offline,
 *                      replayed by the service worker on reconnect.
 *
 *   formDrafts       — JSON snapshots of partially-filled forms so users
 *                      can recover after a crash or forced refresh.
 *                      (Booking wizard already uses Zustand persist /
 *                      localStorage, so this store is reserved for future
 *                      forms that can't use the Zustand layer.)
 *
 * Usage
 * ─────
 *   import { pendingRequestsStore, formDraftsStore } from "@/pwa/db";
 *
 *   // Queue a failed POST for later replay:
 *   await pendingRequestsStore.add({ url, method, body, queuedAt: Date.now() });
 *
 *   // Get all pending items:
 *   const items = await pendingRequestsStore.getAll();
 *
 *   // Remove one after it succeeds:
 *   await pendingRequestsStore.delete(item.id);
 *
 *   // Save a form draft:
 *   await formDraftsStore.put("complaint-form", { field1: "...", field2: "..." });
 *
 *   // Restore a draft:
 *   const draft = await formDraftsStore.get("complaint-form");
 *
 *   // Discard after submit:
 *   await formDraftsStore.delete("complaint-form");
 */

const DB_NAME = "delhi-motors-pwa";
const DB_VERSION = 2; // v1: pendingRequests + formDrafts / v2: + deadLetterQueue

// Migration strategy
// ------------------
// onupgradeneeded is additive only — all store creation is guarded with
// objectStoreNames.contains() so existing data survives upgrades.  Bumping
// DB_VERSION without adding a new guard will cause a failed upgrade; always
// pair a version bump with a matching guard or an explicit data-migration step.
// If a breaking schema change is ever needed for an existing store, read all
// records out of the old store, deleteObjectStore(), recreate, and reinsert.

// ── Singleton connection ────────────────────────────────────────────────────────

let _db = null;

function openDB() {
  if (_db) return Promise.resolve(_db);

  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = (event) => {
      const db = event.target.result;

      // ── pendingRequests store ───────────────────────────────────────────────
      // Keyed by auto-incremented integer id.
      // Holds: { id, url, method, headers, body, tag, queuedAt }
      if (!db.objectStoreNames.contains("pendingRequests")) {
        const store = db.createObjectStore("pendingRequests", {
          keyPath: "id",
          autoIncrement: true,
        });
        // Index by sync tag so the SW can filter by "dm-booking-retry" etc.
        store.createIndex("tag", "tag", { unique: false });
        store.createIndex("queuedAt", "queuedAt", { unique: false });
      }

      // ── formDrafts store ────────────────────────────────────────────────────
      // Keyed by a string form identifier, e.g. "complaint-form".
      // Holds: { id (form key), data, savedAt }
      if (!db.objectStoreNames.contains("formDrafts")) {
        db.createObjectStore("formDrafts", { keyPath: "id" });
      }

      // ── deadLetterQueue store (v2) ──────────────────────────────────────────
      // Items that exhausted MAX_RETRIES or got a permanent error (401/403).
      // Never retried automatically; shown in UI for manual resolution.
      // Holds: { id, url, method, body, reason, failedAt, queuedAt, ... }
      if (!db.objectStoreNames.contains("deadLetterQueue")) {
        const dlq = db.createObjectStore("deadLetterQueue", {
          keyPath: "id",
          autoIncrement: true,
        });
        dlq.createIndex("failedAt", "failedAt", { unique: false });
      }
    };

    req.onsuccess = (event) => {
      _db = event.target.result;
      // If the page outlives the connection, reset so the next call re-opens.
      _db.onclose = () => {
        _db = null;
      };
      resolve(_db);
    };

    req.onerror = () => reject(req.error);
    req.onblocked = () =>
      reject(new Error("IndexedDB upgrade blocked by another tab."));
  });
}

// ── Generic store helpers ──────────────────────────────────────────────────────

function tx(storeName, mode, fn) {
  return openDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const transaction = db.transaction(storeName, mode);
        const store = transaction.objectStore(storeName);
        const req = fn(store);

        // Some operations (getAll, add…) return an IDBRequest; others return
        // IDBIndex requests — handle both.
        transaction.oncomplete = () => resolve(req?.result);
        transaction.onerror = () => reject(transaction.error);
        transaction.onabort = () => reject(transaction.error);
      }),
  );
}

// ── pendingRequests ─────────────────────────────────────────────────────────────

export const pendingRequestsStore = {
  /**
   * Add a serialised request to the queue.
   * @param {{ url: string, method: string, headers?: object, body?: any, tag: string }} item
   * @returns {Promise<number>} auto-generated id
   */
  add(item) {
    return tx("pendingRequests", "readwrite", (store) =>
      store.add({ ...item, queuedAt: Date.now() }),
    );
  },

  /** Return every pending request, oldest first. */
  getAll() {
    return openDB().then(
      (db) =>
        new Promise((resolve, reject) => {
          const transaction = db.transaction("pendingRequests", "readonly");
          const req = transaction
            .objectStore("pendingRequests")
            .index("queuedAt")
            .getAll();
          req.onsuccess = () => resolve(req.result);
          req.onerror = () => reject(req.error);
        }),
    );
  },

  /**
   * Return all requests matching a sync tag.
   * @param {string} tag  e.g. "dm-booking-retry"
   */
  getByTag(tag) {
    return openDB().then(
      (db) =>
        new Promise((resolve, reject) => {
          const transaction = db.transaction("pendingRequests", "readonly");
          const req = transaction
            .objectStore("pendingRequests")
            .index("tag")
            .getAll(tag);
          req.onsuccess = () => resolve(req.result);
          req.onerror = () => reject(req.error);
        }),
    );
  },

  /**
   * Remove a successfully replayed request.
   * @param {number} id
   */
  delete(id) {
    return tx("pendingRequests", "readwrite", (store) => store.delete(id));
  },

  /** Remove all requests matching a tag (called after a batch replay). */
  deleteByTag(tag) {
    return openDB().then(
      (db) =>
        new Promise((resolve, reject) => {
          const transaction = db.transaction("pendingRequests", "readwrite");
          const index = transaction.objectStore("pendingRequests").index("tag");
          const cursorReq = index.openCursor(tag);
          cursorReq.onsuccess = (event) => {
            const cursor = event.target.result;
            if (cursor) {
              cursor.delete();
              cursor.continue();
            }
          };
          transaction.oncomplete = () => resolve();
          transaction.onerror = () => reject(transaction.error);
        }),
    );
  },

  /** Total number of queued requests. */
  count() {
    return openDB().then(
      (db) =>
        new Promise((resolve, reject) => {
          const req = db
            .transaction("pendingRequests", "readonly")
            .objectStore("pendingRequests")
            .count();
          req.onsuccess = () => resolve(req.result);
          req.onerror = () => reject(req.error);
        }),
    );
  },
};

// ── formDrafts ──────────────────────────────────────────────────────────────────

export const formDraftsStore = {
  /**
   * Save (or replace) a draft.
   * @param {string} formKey  Unique form identifier, e.g. "complaint-form"
   * @param {object} data     Serialisable form state
   */
  put(formKey, data) {
    return tx("formDrafts", "readwrite", (store) =>
      store.put({ id: formKey, data, savedAt: Date.now() }),
    );
  },

  /**
   * Retrieve a saved draft, or null if none exists.
   * @param {string} formKey
   * @returns {Promise<object|null>}
   */
  get(formKey) {
    return openDB().then(
      (db) =>
        new Promise((resolve, reject) => {
          const req = db
            .transaction("formDrafts", "readonly")
            .objectStore("formDrafts")
            .get(formKey);
          req.onsuccess = () => resolve(req.result?.data ?? null);
          req.onerror = () => reject(req.error);
        }),
    );
  },

  /**
   * Delete a draft (call after successful submission).
   * @param {string} formKey
   */
  delete(formKey) {
    return tx("formDrafts", "readwrite", (store) => store.delete(formKey));
  },
};

// ── deadLetterQueue ──────────────────────────────────────────────────────────
// Read-only from the page side (the SW writes to it).

export const deadLetterQueueStore = {
  /** Return all permanently failed requests, newest first. */
  getAll() {
    return openDB().then(
      (db) =>
        new Promise((resolve, reject) => {
          const req = db
            .transaction("deadLetterQueue", "readonly")
            .objectStore("deadLetterQueue")
            .getAll();
          req.onsuccess = () =>
            resolve((req.result ?? []).sort((a, b) => b.failedAt - a.failedAt));
          req.onerror = () => reject(req.error);
        }),
    );
  },

  /** Total number of dead-lettered items. */
  count() {
    return openDB().then(
      (db) =>
        new Promise((resolve, reject) => {
          const req = db
            .transaction("deadLetterQueue", "readonly")
            .objectStore("deadLetterQueue")
            .count();
          req.onsuccess = () => resolve(req.result);
          req.onerror = () => reject(req.error);
        }),
    );
  },

  /** Discard a specific dead-lettered item (user acknowledged failure). */
  dismiss(id) {
    return openDB().then(
      (db) =>
        new Promise((resolve, reject) => {
          const tx = db.transaction("deadLetterQueue", "readwrite");
          tx.objectStore("deadLetterQueue").delete(id);
          tx.oncomplete = () => resolve();
          tx.onerror = () => reject(tx.error);
        }),
    );
  },

  /** Discard all dead-lettered items. */
  dismissAll() {
    return openDB().then(
      (db) =>
        new Promise((resolve, reject) => {
          const tx = db.transaction("deadLetterQueue", "readwrite");
          tx.objectStore("deadLetterQueue").clear();
          tx.oncomplete = () => resolve();
          tx.onerror = () => reject(tx.error);
        }),
    );
  },

  /**
   * Move a dead-lettered item back into the pending queue so the SW will
   * attempt it again.  Strips dead-letter metadata and resets retry counters
   * so it gets a fresh set of MAX_RETRIES attempts.
   *
   * This is an atomic cross-store transaction — both the delete and the add
   * succeed together or neither does.
   *
   * @param {number} id  Dead-letter record id
   */
  requeue(id) {
    return openDB().then(
      (db) =>
        new Promise((resolve, reject) => {
          const transaction = db.transaction(
            ["deadLetterQueue", "pendingRequests"],
            "readwrite",
          );
          const dlqStore = transaction.objectStore("deadLetterQueue");
          const prStore = transaction.objectStore("pendingRequests");

          const getReq = dlqStore.get(id);
          getReq.onsuccess = () => {
            const item = getReq.result;
            if (!item) {
              transaction.abort();
              return;
            }
            // Remove dead-letter–specific fields; reset retry state so it
            // gets a full fresh attempt window.
            // eslint-disable-next-line no-unused-vars
            const { id: _id, reason, failedAt, ...rest } = item;
            prStore.add({
              ...rest,
              retryCount: 0,
              nextRetryAt: 0,
              queuedAt: Date.now(),
            });
            dlqStore.delete(id);
          };

          transaction.oncomplete = () => resolve();
          transaction.onerror = () => reject(transaction.error);
          transaction.onabort = () =>
            reject(new Error("requeue transaction aborted"));
        }),
    );
  },
};
